// Helpers for the access/refresh token pair kept in localStorage.
//
// A refresh request can still be in flight when the user logs out and someone else logs in
// (in this tab or another). Its response carries the previous user's tokens, so writing it
// blindly would silently switch the browser back to that user. Every refresh therefore
// stores its result only if the refresh token it sent is still the current one.

// Reads a JWT's claims without verifying it; null if the token can't be decoded.
const readTokenClaims = (token) => {
    if (!token || typeof token !== 'string') return null;
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(json) || null;
    } catch {
        return null;
    }
};

// Reads the userId claim from a JWT without verifying it; only used to compare sessions.
export const getTokenUserId = (token) => {
    const userId = readTokenClaims(token)?.userId;
    return userId ? String(userId) : null;
};

// True once the token's exp claim has passed on this device's clock. Tokens without an
// exp claim (no JWT_EXPIRES_IN on the server) never expire. Sending an expired token only
// earns a 401 that the server writes to the error log, so callers refresh first instead.
export const isTokenExpired = (token, now = Date.now()) => {
    const exp = readTokenClaims(token)?.exp;
    return typeof exp === 'number' && exp * 1000 <= now;
};

// True while localStorage still holds the session that sentRefreshToken belongs to.
export const isSameSession = (sentRefreshToken) =>
    !!sentRefreshToken && localStorage.getItem('refreshToken') === sentRefreshToken;

// Stores rotated tokens only if the session they were issued for is still current.
// Returns false when the session changed meanwhile (logout, another login, another tab).
export const storeRefreshedTokens = (sentRefreshToken, tokens) => {
    if (!tokens?.accessToken || !tokens?.refreshToken) return false;
    if (!isSameSession(sentRefreshToken)) return false;
    localStorage.setItem('token', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    return true;
};

// After a refresh lost a race: the access token to retry with if storage now holds a
// session for the same user (e.g. another tab already rotated it), otherwise null.
export const currentTokenForSameUser = (sentRefreshToken) => {
    const current = localStorage.getItem('token');
    const sentUserId = getTokenUserId(sentRefreshToken);
    if (!current || !sentUserId) return null;
    return getTokenUserId(current) === sentUserId ? current : null;
};
